import { PillProps } from '@/app/components/ui/Pill';
import { ProgressBarProps } from '@/app/components/ui/ProgressBar';

export const colorNames = ['blue', 'orange', 'sky', 'black', 'green', 'destructive'] as const;

export type ColorName = (typeof colorNames)[number];

export type Variant = 'default' | 'secondary' | 'outline' | 'ghost' | 'link';

export const colors: Record<ColorName, Record<Variant, string>> = {
  blue: {
    default: 'bg-primary text-white hover:bg-primary',
    secondary:
      'bg-blue-alpha-10 border border-blue-alpha-10 text-blue-base hover:bg-background hover:border-blue-base',
    outline:
      'bg-transparent border border-primary text-primary hover:text-primary hover:bg-primary/10 disabled:border-primary/50 disabled:bg-transparent disabled:text-primary/50',
    ghost: 'text-blue-base hover:bg-blue-alpha-10',
    link: 'text-blue-base',
  },
  orange: {
    default: 'bg-orange-base text-white hover:bg-orange-darker',
    secondary:
      'bg-orange-alpha-10 border border-orange-alpha-10 text-orange-base hover:bg-background hover:border-orange-base',
    outline:
      'border border-orange-base text-orange-base hover:text-orange-base hover:bg-orange-alpha-10 disabled:border-secondary',
    ghost: 'text-orange-base hover:bg-orange-alpha-10',
    link: 'text-orange-base',
  },
  sky: {
    default: 'bg-sky-base text-white hover:bg-sky-darker',
    secondary:
      'bg-sky-alpha-10 border border-sky-alpha-10 text-sky-base hover:bg-background hover:border-sky-base',
    outline:
      'border border-sky-base text-sky-base hover:text-sky-base hover:bg-sky-alpha-10 disabled:border-secondary',
    ghost: 'text-sky-base hover:bg-sky-alpha-10',
    link: 'text-sky-base',
  },
  black: {
    default: 'bg-black-base text-white hover:bg-black-darker',
    secondary:
      'bg-secondary border border-weak-50 text-foreground-base hover:bg-background hover:border-border',
    outline:
      'border border-border text-foreground-base hover:text-foreground-base hover:bg-black-alpha-10 hover:border-black-alpha-10 bg-transparent disabled:border-secondary',
    ghost: 'text-foreground-base hover:bg-black-alpha-10',
    link: 'text-foreground-base',
  },
  green: {
    default: 'bg-green-base text-white hover:bg-green-darker',
    secondary:
      'bg-green-alpha-10 border border-green-alpha-10 text-green-base hover:bg-background hover:border-green-base',
    outline:
      'border border-green-base text-green-base hover:text-green-base hover:bg-green-alpha-10 disabled:border-secondary',
    ghost: 'text-green-base hover:bg-green-alpha-10',
    link: 'text-green-base',
  },
  destructive: {
    default: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    secondary:
      'border border-destructive/10 bg-destructive/10 text-destructive hover:border-destructive hover:bg-background',
    outline:
      'border border-destructive bg-transparent text-destructive hover:bg-destructive/10 hover:text-destructive disabled:border-secondary',
    ghost: 'text-destructive hover:bg-destructive/10 hover:text-destructive',
    link: 'text-destructive',
  },
};

export const signalColorHex = {
  website: '#3981F6',
  market: '#A855F7',
  linkedin: '#6366F1',
} as const;

export const signalSoftClassName = {
  website: 'bg-primary/10 text-primary',
  market: 'bg-purple-500/10 text-purple-500',
  linkedin: 'bg-indigo-500/10 text-indigo-500',
} as const;

export const signalTextClassName = {
  website: 'text-primary',
  market: 'text-purple-500',
  linkedin: 'text-indigo-500',
} as const;

export const signalPillColor = {
  website: 'info',
  market: 'purple',
  linkedin: 'indigo',
} satisfies Record<keyof typeof signalColorHex, PillProps['color']>;

export const mattonboardingStepsStatusColor: Record<string, PillProps['color']> = {
  completed: 'success',
  in_progress: 'warning',
  not_started: 'neutral',
};

export const clientStatusColor: Record<string, PillProps['color']> = {
  not_started: 'neutral',
  onboarding: 'warning',
  live: 'success',
  disabled: 'destructive',
  in_progress: 'warning',
  completed: 'success',
};

export const agencyStatusColor: Record<string, PillProps['color']> = {
  active: 'success',
  inactive: 'destructive',
};

export const companyStatusColor: Record<string, PillProps['color']> = {
  target: 'warning',
  engaged: 'info',
  converted: 'success',
  archived: 'neutral',
  excluded: 'destructive',
};

export const companyStatusIconColor: Record<string, string> = {
  target: 'text-away-dark',
  engaged: 'text-information-dark',
  converted: 'text-success-dark',
  archived: 'text-faded-dark',
  excluded: 'text-error-dark',
};

export const contactStatusColor: Record<string, PillProps['color']> = {
  target: 'warning',
  engaged: 'info',
  converted: 'success',
  archived: 'neutral',
  excluded: 'destructive',
};

export const contactStatusIconColor: Record<string, string> = {
  target: 'text-away-dark',
  engaged: 'text-information-dark',
  converted: 'text-success-dark',
  archived: 'text-faded-dark',
  excluded: 'text-error-dark',
};

export const campaignStatusColor = {
  started: 'text-success-base',
  completed: 'text-success-base',
  paused: 'text-away-base',
  stopped: 'text-error-base',
  archived: 'text-error-base',
};

export const campaignStatusPillColor = {
  draft: 'text-away-base',
  not_started: 'text-faded-base',
  ready: 'text-success-base',
  started: 'text-information-base',
  running: 'text-success-base',
  completed: 'text-success-base',
  failed: 'text-error-base',
  paused: 'text-warning-base',
  ended: 'text-error-base',
  error: 'text-error-base',
  scheduled: 'text-away-base',
  archived: 'text-error-base',
};

export const campaignChannelColorMap = {
  email: 'info',
  call: 'success',
  slack: 'neutral',
  linkedin: 'info',
};

export const conversationTypeColorMap: Record<string, PillProps['color']> = {
  'email-draft': 'warning',
  'email-review': 'warning',
  'email-outgoing': 'success',
  'email-incoming': 'success',
  'linkedin-outgoing': 'info',
  'linkedin-incoming': 'info',
  'linkedin-draft': 'warning',
  archived: 'destructive',
  'linkedin-not_delivered': 'neutral',
  'email-not_delivered': 'neutral',
  'imessage-outgoing': 'success',
  'imessage-incoming': 'success',
  'imessage-draft': 'warning',
};

export const conversationTimelineColorMap: Record<string, PillProps['color']> = {
  'email-draft': 'warning',
  'email-review': 'warning',
  'email-outgoing': 'success',
  'email-incoming': 'success',
  'linkedin-outgoing': 'info',
  'linkedin-incoming': 'info',
  'linkedin-draft': 'warning',
  archived: 'destructive',
  'linkedin-not_delivered': 'neutral',
  'email-not_delivered': 'neutral',
  'imessage-draft': 'warning',
  'imessage-outgoing': 'success',
  'imessage-incoming': 'success',
};

export const conversationCardBorderColorMap: Record<string, string> = {
  'email-draft': 'bg-away-base',
  'email-review': 'bg-away-base',
  'email-outgoing': 'bg-transparent',
  'email-incoming': 'bg-success',
  'linkedin-outgoing': 'bg-transparent',
  'linkedin-incoming': 'bg-success',
  'linkedin-draft': 'bg-away-light',
  archived: 'bg-transparent',
  'imessage-outgoing': 'bg-transparent',
  'imessage-incoming': 'bg-success',
  'imessage-draft': 'bg-away-light',
};

export const taskTypeColors: Record<string, PillProps['color']> = {
  follow_up: 'info',
};

export const intentTypeColor: Record<string, string> = {
  neutral: 'text-purple-700',
  positive: 'text-green-700',
  negative: 'text-red-700',
};

export const intentReasonColor: Record<string, string> = {
  positive: 'text-information-dark',
  negative: 'text-error-dark',
  neutral: 'text-feature-dark',
};

export const taskPriorityColors: Record<string, PillProps['color']> = {
  low: 'success',
  medium: 'warning',
  high: 'destructive',
  urgent: 'destructive',
};

export const taskChannelColors: Record<string, PillProps['color']> = {
  email: 'success',
  linkedin: 'info',
  imessage: 'success',
};

export const taskStatusColors: Record<string, PillProps['color']> = {
  completed: 'success',
  in_progress: 'warning',
  planned: 'info',
};

export const pipelineStageColors: Record<string, PillProps['color']> = {
  intro: 'neutral',
  discussion: 'info',
  closing: 'purple',
};

export const campaignStatusIconColors: Record<string, string> = {
  draft: 'text-away-base',
  not_started: 'text-faded-base',
  in_progress: 'text-away-base',
  completed: 'text-success-base',
  running: 'text-success-base',
  paused: 'text-error-base',
  ended: 'text-error-base',
  error: 'text-error-base',
  scheduled: 'text-away-base',
  archived: 'text-warning-base',
  unarchive: 'text-feature-base',
};

export const campaignStatusPillColors: Record<string, PillProps['color']> = {
  draft: 'warning',
  not_started: 'neutral',
  in_progress: 'warning',
  completed: 'success',
  ready: 'success',
  running: 'success',
  paused: 'warning',
  ended: 'destructive',
  error: 'destructive',
  scheduled: 'warning',
  archived: 'neutral',
};

export const integrationWorkflowStatusPillColors: Record<string, PillProps['color']> = {
  not_running: 'neutral',
  running: 'success',
  deleted: 'destructive',
};

export const targetListWorkflowStatusPillColors: Record<string, PillProps['color']> = {
  ...integrationWorkflowStatusPillColors,
  building: 'info',
  draft: 'warning',
  error: 'destructive',
  deleted: 'destructive',
};

export const userStatusColors: Record<string, string> = {
  pending: 'text-away-base',
  active: 'text-success-base',
  suspended: 'text-warning-base',
  deleted: 'text-faded-base',
};

export const userStatusPillColors: Record<string, PillProps['color']> = {
  active: 'success',
  pending: 'warning',
  suspended: 'destructive',
  deleted: 'destructive',
};

export const userRolePillColors: Record<string, PillProps['color']> = {
  admin: 'info',
  marketer: 'cyan',
  viewer: 'default',
};

export const targetListStatusColors: Record<string, PillProps['color']> = {
  ready: 'success',
  draft: 'warning',
  failed: 'destructive',
  building: 'info',
  archived: 'neutral',
};

export const targetListStatusPillProps: Record<string, Pick<PillProps, 'variant' | 'color'>> = {
  running: { variant: 'soft', color: integrationWorkflowStatusPillColors.running },
  not_running: { variant: 'soft', color: integrationWorkflowStatusPillColors.not_running },
  error: { variant: 'soft', color: 'destructive' },
  deleted: { variant: 'soft', color: 'neutral' },
  draft: { variant: 'soft', color: 'warning' },
  failed: { variant: 'soft', color: 'destructive' },
  building: { variant: 'soft', color: 'info' },
  archived: { variant: 'soft', color: 'neutral' },
};

export const initiativeStatusColor: Record<string, PillProps['color']> = {
  draft: 'neutral',
  active: 'success',
  archived: 'destructive',
};

export const userLinkedinStatusColors: Record<string, PillProps['color']> = {
  connected: 'success',
  not_connected: 'neutral',
  disconnected: 'destructive',
  connecting: 'info',
};

export const userTableChannelBadgeClassNames: Record<string, string> = {
  success: 'border-border text-foreground',
  warning: 'border-border text-foreground',
  destructive: 'border-border text-foreground',
  neutral: 'border-border text-foreground',
};

export const userTableChannelIconClassNames: Record<string, string> = {
  success: 'text-primary',
  warning: 'text-warning',
  destructive: 'text-destructive',
  neutral: 'text-muted-foreground',
};

export const userTableChannelStatusIconClassNames: Record<string, string> = {
  success: 'text-success',
  destructive: 'text-destructive',
};

export const userTableChannelCountClassNames: Record<string, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  neutral: 'bg-muted text-muted-foreground',
};

export const consoleItemPillColors: Record<string, PillProps['color']> = {
  failed: 'destructive',
  pending: 'warning',
  completed: 'success',
  skipped: 'neutral',
};

export const consoleTextColors: Record<string, string> = {
  failed: 'text-error-base',
  pending: 'text-away-base',
  completed: 'text-strong-500',
  skipped: 'text-gray-500',
};

export const consoleNodePillColors: Record<string, PillProps['color']> = {
  email: 'success',
  linkedin: 'info',
  imessage: 'success',
  condition: 'warning',
  start: 'purple',
  end: 'success',
};

export const campaignProgressVariant: Record<string, ProgressBarProps['variant']> = {
  running: 'success',
  paused: 'warning',
  stopped: 'grey',
  archived: 'grey',
  completed: 'success',
  not_started: 'grey',
};

export const campaignInsightProgressColors = {
  completed: {
    dot: 'bg-success',
    text: 'text-success',
    bar: 'bg-success',
  },
  pending: {
    dot: 'bg-warning',
    text: 'text-warning',
  },
  failed: {
    dot: 'bg-destructive',
    text: 'text-destructive',
  },
};

export const campaignInsightChannelStatusPillProps: Record<
  string,
  Pick<PillProps, 'variant' | 'color'>
> = {
  connected: { variant: 'soft', color: 'success' },
  disconnected: { variant: 'soft', color: 'destructive' },
  inactive: { variant: 'soft', color: 'neutral' },
  pending: { variant: 'soft', color: 'warning' },
};

export const userMailboxIconColors: Record<string, string> = {
  connected: 'text-success-base',
  inactive: 'text-muted-foreground',
  pending: 'text-warning-base',
  disconnected: 'text-error-base',
};

export const signalEntityPillProps: Record<string, Pick<PillProps, 'variant' | 'color'>> = {
  linkedin: { variant: 'soft', color: 'info' },
  email: { variant: 'soft', color: 'success' },
  form: { variant: 'soft', color: 'purple' },
  page: { variant: 'soft', color: 'purple' },
  response: { variant: 'soft', color: 'info' },
  imessage: { variant: 'soft', color: 'success' },
  icp: { variant: 'soft', color: 'success' },
  persona: { variant: 'soft', color: 'info' },
};

export const conversationNavIconColors: Record<string, string> = {
  all: 'text-information-base',
  draft: 'text-warning',
  positive: 'text-success-base',
  negative: 'text-warning-base',
  neutral: 'text-feature-base',
  discarded: 'text-error-base',
  not_delivered: 'text-faded-dark',
};

export const insightChannelColors: Record<string, string> = {
  email: '#1fc16b',
  linkedin: '#3559e9',
  imessage: '#26d043',
};

export const insightEmailDonutColors = {
  draft: '#D97706',
  delivered: '#0b4627',
  opens: '#1fc16b',
  responses: '#6ee7a0',
  not_delivered: '#bbf7d0',
  unsubscribes: '#d1d1d1',
};

export const insightLinkedinDonutColors = {
  connections: '#122368',
  accepted: '#3559e9',
  not_accepted: '#93b4fd',
  responses: '#c7d7fe',
};

export const insightImessageDonutColors = {
  delivered: '#0b4627',
  opens: '#26d043',
  responses: '#6ee7a0',
  not_delivered: '#bbf7d0',
};

export const planStatusBadgeClassNames: Record<string, string> = {
  active: 'bg-[#E0FAEC] text-[#0B4627]',
  inactive: 'bg-secondary text-[#171717]',
  canceled: 'bg-[#FFEBEC] text-[#9F1C21]',
  payment_failed: 'bg-[#FFFAEB] text-[#624C18]',
};

export type NormalizedPlanStatus = keyof typeof planStatusBadgeClassNames;

export const creditUsageToneThresholds = {
  warning: 90,
};

export const creditUsageToneClassNames = {
  default: {
    badge:
      'rounded-full border-0 bg-[#D0FBE9] px-2 py-0.5 text-[12px] font-medium leading-4 text-[#0B4627]',
  },
  warning: {
    badge:
      'rounded-full border-0 bg-warning/10 px-2 py-0.5 text-[12px] font-medium leading-4 text-warning',
  },
  canceled: {
    badge:
      'rounded-full border-0 bg-destructive/10 px-2 py-0.5 text-[12px] font-medium leading-4 text-destructive',
  },
};

export type SidebarPlanCardState =
  | 'active'
  | 'active_warning'
  | 'credits_used'
  | 'payment_failed'
  | 'canceled';

export const subscriptionStateTranslationKeys: Record<SidebarPlanCardState, string> = {
  active: 'active',
  active_warning: 'active',
  credits_used: 'creditsUsed',
  payment_failed: 'paymentFailed',
  canceled: 'canceled',
};

export const getSubscriptionState = ({
  status,
  remainingCredits,
  usagePercentage = 0,
}: {
  status?: string | null;
  remainingCredits?: number;
  usagePercentage?: number;
}): SidebarPlanCardState => {
  if (status === 'payment_failed') return 'payment_failed';
  if (status === 'canceled') return 'canceled';
  if (remainingCredits !== undefined && remainingCredits === 0) return 'credits_used';
  if (usagePercentage >= creditUsageToneThresholds.warning) return 'active_warning';
  return 'active';
};

export const sidebarPlanCardStateClassNames: Record<
  SidebarPlanCardState,
  {
    card: string;
    text: string;
    textMuted: string;
    pill: string;
    progress?: string;
    button: string;
    showUsage: boolean;
    usageBadge: string;
    usageProgress: string;
    planBadge: string;
    normalizedStatus: NormalizedPlanStatus;
  }
> = {
  active: {
    card: 'bg-primary/10 border border-primary/20',
    text: 'text-sidebar-foreground',
    textMuted: 'text-muted-foreground',
    pill: 'success',
    progress: '[&>div:first-child]:bg-primary/20 [&>div:first-child>div]:bg-primary',
    button: 'text-white bg-primary hover:bg-primary hover:text-white',
    showUsage: true,
    usageBadge: creditUsageToneClassNames.default.badge,
    usageProgress: 'h-2 rounded-[2px] bg-primary/20 [&>div]:bg-primary',
    planBadge: planStatusBadgeClassNames.active,
    normalizedStatus: 'active',
  },
  active_warning: {
    card: 'bg-warning/10 border border-warning/20',
    text: 'text-sidebar-foreground',
    textMuted: 'text-muted-foreground',
    pill: 'success',
    progress: '[&>div:first-child]:bg-warning/20 [&>div:first-child>div]:bg-warning',
    button: 'border-warning text-warning hover:bg-warning hover:text-white',
    showUsage: true,
    usageBadge: creditUsageToneClassNames.warning.badge,
    usageProgress: 'h-2 rounded-[2px] bg-warning/20 [&>div]:bg-warning',
    planBadge: planStatusBadgeClassNames.active,
    normalizedStatus: 'active',
  },
  credits_used: {
    card: 'bg-destructive/10 border border-destructive/20',
    text: 'text-sidebar-foreground',
    textMuted: 'text-muted-foreground',
    pill: 'destructive',
    progress: '[&>div:first-child]:bg-destructive/20 [&>div:first-child>div]:bg-destructive',
    button: 'border-destructive text-destructive hover:bg-destructive hover:text-white',
    showUsage: true,
    usageBadge: creditUsageToneClassNames.canceled.badge,
    usageProgress: 'h-2 rounded-[2px] bg-destructive/20 [&>div]:bg-destructive',
    planBadge: planStatusBadgeClassNames.inactive,
    normalizedStatus: 'inactive',
  },
  payment_failed: {
    card: 'bg-destructive/10 border border-destructive/20',
    text: 'text-sidebar-foreground',
    textMuted: 'text-muted-foreground',
    pill: 'destructive',
    button: 'border-destructive text-destructive hover:bg-destructive hover:text-white',
    showUsage: false,
    usageBadge: creditUsageToneClassNames.canceled.badge,
    usageProgress: 'h-2 rounded-[2px] bg-destructive/20 [&>div]:bg-destructive',
    planBadge: planStatusBadgeClassNames.payment_failed,
    normalizedStatus: 'payment_failed',
  },
  canceled: {
    card: 'bg-destructive/10 border border-destructive/20',
    text: 'text-sidebar-foreground',
    textMuted: 'text-muted-foreground',
    pill: 'destructive',
    button: 'border-destructive text-destructive hover:bg-destructive hover:text-white',
    showUsage: false,
    usageBadge: creditUsageToneClassNames.canceled.badge,
    usageProgress: 'h-2 rounded-[2px] bg-destructive/20 [&>div]:bg-destructive',
    planBadge: planStatusBadgeClassNames.canceled,
    normalizedStatus: 'canceled',
  },
};

export const userConnectStatusColors: Record<string, PillProps['color']> = {
  connected: 'success',
  connecting: 'warning',
  not_connected: 'destructive',
  disconnected: 'destructive',
  archived: 'destructive',
};
